import React, { useMemo } from 'react';
import { MantineReactTable, type MRT_ColumnDef, type MRT_Row, useMantineReactTable } from 'mantine-react-table';
import { MantineProvider, Box, Button, Group } from '@mantine/core';
import { MRT_Localization_ES } from 'mantine-react-table/locales/es';
import { useT } from '../i18n/admin';

interface MantineDataTableProps<T extends Record<string, any>> {
  columns: MRT_ColumnDef<T>[];
  data: T[];
  loading?: boolean;
  enablePagination?: boolean;
  enableSorting?: boolean;
  enableColumnFilters?: boolean;
  enableGlobalFilter?: boolean;
  initialPageSize?: number;
  pageSizeOptions?: number[];
  onRowClick?: (row: T) => void;
  // Right-click on a row (e.g. a quick-actions menu at the cursor). The
  // browser's own context menu is suppressed only when this is passed.
  onRowContextMenu?: (row: T, event: React.MouseEvent<HTMLTableRowElement>) => void;
  manualPagination?: boolean;
  pageCount?: number;
  onPaginationChange?: (pagination: { pageIndex: number; pageSize: number }) => void;
  state?: {
    pagination?: {
      pageIndex: number;
      pageSize: number;
    };
    columnVisibility?: Record<string, boolean>;
  };
  // Custom toolbar props
  renderTopToolbarCustomActions?: () => React.ReactNode;
  totalRecords?: number;
  initialState?: {
    columnVisibility?: Record<string, boolean>;
  };
  // Custom container height
  containerHeight?: string;
  // Row expanding (tree rows). `getSubRows` returns the child rows of a row;
  // rows without children render no chevron. The expand chevron stops click
  // propagation, so `onRowClick` is not triggered when toggling.
  enableExpanding?: boolean;
  getSubRows?: (originalRow: T, index: number) => T[] | undefined;
  // SAM-281: override the "no records" empty-state text, e.g. to name the
  // active search term when a server-side search returns zero matches.
  emptyStateMessage?: string;
}

export function MantineDataTable<T extends Record<string, any>>({
  columns,
  data,
  loading = false,
  enablePagination = true,
  enableSorting = true,
  enableColumnFilters = false,
  enableGlobalFilter = true,
  initialPageSize = 20,
  pageSizeOptions = [10, 20, 50, 100],
  onRowClick,
  onRowContextMenu,
  manualPagination = false,
  pageCount,
  onPaginationChange,
  state,
  renderTopToolbarCustomActions,
  totalRecords,
  initialState: customInitialState,
  containerHeight = 'calc(100vh - 300px)',
  enableExpanding = false,
  getSubRows,
  emptyStateMessage,
}: MantineDataTableProps<T>) {
  const { lang } = useT();
  const table = useMantineReactTable({
    columns,
    data,
    enablePagination,
    enableSorting,
    enableColumnFilters,
    enableGlobalFilter,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    enableHiding: true, // Enable column hiding
    enableTopToolbar: enableGlobalFilter || !!renderTopToolbarCustomActions, // Only show toolbar if search or custom actions are enabled
    ...(enableExpanding
      ? {
          enableExpanding: true,
          enableExpandAll: false,
          getSubRows,
          displayColumnDefOptions: {
            'mrt-row-expand': { size: 40, header: '' },
          },
          mantineExpandButtonProps: ({ row }: { row: MRT_Row<T> }) => ({
            // Hide the chevron entirely on rows that have nothing to expand.
            sx: row.getCanExpand() ? undefined : { visibility: 'hidden' },
          }),
        }
      : {}),
    initialState: {
      density: 'xs',
      pagination: { pageSize: initialPageSize, pageIndex: 0 },
      ...customInitialState, // Merge custom initial state
    },
    // For manual (server-side) pagination: fully controlled state
    // For client-side pagination: NO state prop — let MRT handle it internally
    ...(manualPagination
      ? {
          state: {
            isLoading: loading,
            showProgressBars: false,
            showSkeletons: false,
            showLoadingOverlay: loading,
            ...state,
          },
          manualPagination: true,
          pageCount,
          rowCount: totalRecords,
          onPaginationChange,
        }
      : {
          manualPagination: false,
          // SAM-223: even with client-side pagination the table must show a
          // loading overlay while data is in flight. Without wiring `isLoading`
          // here MRT renders its "No records to display" empty state during the
          // initial fetch, which reads as "there are no users".
          state: {
            isLoading: loading,
            showProgressBars: false,
            showSkeletons: false,
            showLoadingOverlay: loading,
            ...state,
          },
        }
    ),
    mantineTableProps: {
      striped: true,
      highlightOnHover: true,
      withColumnBorders: true,
      withBorder: true,
      sx: {
        '& thead tr th': {
          backgroundColor: '#FFFFFF',
          color: '#374151',
          fontSize: '12px',
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
          borderBottom: '2px solid #E5E7EB',
          padding: '12px 16px',
          verticalAlign: 'middle',
        },
        // Align header content to use flexbox with space-between
        '& thead tr th > div': {
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
        },
        // Move sort icon to the right
        '& thead tr th .mantine-TableHeadCell-Content': {
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
          gap: '8px',
        },
        '& tbody tr td': {
          fontSize: '14px',
          fontWeight: 400,
          color: '#1F2937',
          padding: '12px 16px',
          borderBottom: '1px solid #F3F4F6',
          verticalAlign: 'middle',
        },
        '& tbody tr': {
          height: '52px',
        },
        '& tbody tr:nth-of-type(odd)': {
          backgroundColor: '#FFFFFF',
        },
        '& tbody tr:nth-of-type(even)': {
          backgroundColor: '#F3F4F6',
        },
        '& tbody tr:hover': {
          backgroundColor: '#F3F4F6 !important',
          cursor: onRowClick ? 'pointer' : 'default',
        },
        '& tbody tr:focus-visible': {
          outline: '2px solid var(--tenant-primary, #000)',
          outlineOffset: '-2px',
        },
      },
    },
    // SAM-79 / SAM-80: the paper must fill its (height-bounded) parent as a
    // flex column so the bottom pagination toolbar — the "Rows per page" control
    // and the "1-N of M" count — stays pinned and visible. Previously the paper
    // sized to its natural height (toolbar + a fixed `calc(100vh - 300px)`
    // scroll area + footer), which overflowed the page's `overflow:hidden`
    // container and clipped the footer off the bottom of the screen.
    mantinePaperProps: {
      sx: {
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      },
    },
    mantineTableContainerProps: {
      sx: {
        // Flex-fill the available space instead of a fixed viewport calc, and
        // keep `containerHeight` only as an upper cap. `minHeight: 0` lets the
        // container shrink so the footer below it is never pushed off-screen.
        flex: '1 1 auto',
        minHeight: 0,
        maxHeight: containerHeight,
        overflow: 'auto',
        position: 'relative',
        zIndex: 1, // Low z-index for container
      },
    },
    mantineTableBodyRowProps: ({ row }: { row: MRT_Row<T> }) => ({
      ...(onRowClick
        ? {
            onClick: () => onRowClick(row.original),
            // Clickable rows are reachable and openable from the keyboard.
            tabIndex: 0,
            onKeyDown: (e: React.KeyboardEvent<HTMLTableRowElement>) => {
              if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) {
                e.preventDefault();
                onRowClick(row.original);
              }
            },
          }
        : {}),
      ...(onRowContextMenu
        ? {
            onContextMenu: (e: React.MouseEvent<HTMLTableRowElement>) => {
              e.preventDefault();
              onRowContextMenu(row.original, e);
            },
          }
        : {}),
      // Child (expanded) rows get a subtle tint so they read as nested under
      // their parent instead of as independent records.
      ...(row.depth > 0
        ? { sx: { '& td': { backgroundColor: '#FAFBFC' } } }
        : {}),
    }),
    mantinePaginationProps: {
      rowsPerPageOptions: pageSizeOptions.map(String),
      showRowsPerPage: true,
      sx: {
        padding: '10px 16px',
        borderTop: '1px solid #F0F0F0',
        zIndex: 1, // Low z-index so dropdowns can appear above
      },
    },
    mantineSearchTextInputProps: {
      placeholder: 'Search...',
      sx: {
        marginBottom: '16px',
        '& input': {
          borderRadius: '8px',
          backgroundColor: '#F3F3F3',
          border: 'none',
          '&:hover': {
            backgroundColor: '#fff',
            borderColor: '#E2E8F0',
          },
          '&:focus': {
            backgroundColor: '#fff',
            borderColor: '#3182CE',
          },
        },
      },
    },
    // Custom toolbar rendering
    renderTopToolbarCustomActions: renderTopToolbarCustomActions ? () => (
      <Group spacing="md" style={{ flex: 1, justifyContent: 'flex-end' }}>
        {renderTopToolbarCustomActions()}
      </Group>
    ) : undefined,
    // Show record count in bottom toolbar
    renderBottomToolbarCustomActions: totalRecords !== undefined ? ({ table }) => {
      const { pageIndex, pageSize } = table.getState().pagination;
      const firstRecord = pageIndex * pageSize + 1;
      const lastRecord = Math.min((pageIndex + 1) * pageSize, totalRecords);

      // While the first page is in flight `totalRecords` is still 0, and the
      // counter read "1-0 of 0" — "there is nothing here" — until the rows
      // landed. Say nothing until there is something to count.
      if (loading && !totalRecords) return null;

      return (
        <Box style={{ 
          fontSize: '14px', 
          color: '#6B7280',
          padding: '0 16px',
        }}>
          {firstRecord}-{lastRecord} of {totalRecords}
        </Box>
      );
    } : undefined,
    // Custom loading overlay with black spinner over the entire table
    mantineLoadingOverlayProps: {
      loaderProps: {
        color: 'dark',
        size: 'lg',
      },
    },
    // Hide the linear progress in the toolbar
    mantineProgressProps: {
      sx: {
        display: 'none',
      },
    },
    // SAM-281: let the caller name the active search term in the empty state
    // ("No matches found for ...") instead of the generic default.
    // SAM-683: the table's own words (sorting, pagination, empty state) follow the admin language.
    localization: {
      ...(lang === 'es' ? MRT_Localization_ES : {}),
      ...(emptyStateMessage ? { noRecordsToDisplay: emptyStateMessage } : {}),
    } as any,
  });

  return (
    <MantineProvider
      theme={{
        primaryColor: 'dark',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      }}
    >
      <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', position: 'relative', zIndex: 1 }}>
        <MantineReactTable table={table} />
      </div>
    </MantineProvider>
  );
}
